import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Pencil, Trash2 } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { DeckFormDialog } from "@/components/flashcards/DeckFormDialog"
import { QuizEditor } from "@/components/quizzes/QuizEditor"
import { questionCount } from "@/components/quizzes/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { ContentAreaGroup } from "@/types/flashcards"
import type { QuizDetail } from "@/types/quizzes"

export default function AdminQuizShow({
  quiz,
  content_area_groups,
}: {
  quiz: QuizDetail
  content_area_groups: ContentAreaGroup[]
}) {
  const [editing, setEditing] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const basePath = `/admin/quizzes/${quiz.id}`
  const published = quiz.status === "published"

  return (
    <>
      <Head title={`${quiz.title} (official quiz)`}>
        <meta name="description" content={`Manage the questions and publishing status of the official quiz ${quiz.title}.`} />
        <meta property="og:title" content={`${quiz.title} (official quiz)`} />
        <meta property="og:description" content={`Manage the questions and publishing status of the official quiz ${quiz.title}.`} />
      </Head>
      <AdminShell>
        <Link href="/admin/quizzes" className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> Official quizzes
        </Link>
        <PageHeader
          title={quiz.title}
          description={
            <>
              <Badge tone={published ? "accent" : "muted"} className="mr-2">
                {published ? "Published" : "Draft"}
              </Badge>
              {quiz.ai_draft && (
                <Badge tone="signal" className="mr-2">
                  AI draft – needs review
                </Badge>
              )}
              {questionCount(quiz.question_count)}
              {quiz.description ? ` · ${quiz.description}` : ""}
            </>
          }
          actions={
            <>
              <Button type="button" variant="ghost" size="icon" aria-label="Edit quiz" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" aria-label="Delete quiz" onClick={() => setDeleting(true)}>
                <Trash2 className="h-4 w-4" />
              </Button>
              {quiz.ai_draft && (
                <Button type="button" variant="secondary" onClick={() => router.patch(`${basePath}/mark_reviewed`)}>
                  Mark reviewed
                </Button>
              )}
              {published ? (
                <Button type="button" variant="secondary" onClick={() => router.patch(`${basePath}/unpublish`)}>
                  Unpublish
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={quiz.question_count === 0 || quiz.ai_draft}
                  title={quiz.ai_draft ? "Mark it reviewed first" : quiz.question_count === 0 ? "Add at least one question first" : undefined}
                  onClick={() => router.patch(`${basePath}/publish`)}
                >
                  Publish
                </Button>
              )}
            </>
          }
        />

        <QuizEditor quiz={quiz} basePath={basePath} contentAreaGroups={content_area_groups} />

        <DeckFormDialog
          open={editing}
          onOpenChange={setEditing}
          action={basePath}
          method="patch"
          title="Edit quiz"
          submitLabel="Save"
          initial={{ title: quiz.title, description: quiz.description }}
        />
        <ConfirmDialog
          open={deleting}
          onOpenChange={setDeleting}
          title="Delete this official quiz?"
          description={`"${quiz.title}", its ${questionCount(quiz.question_count)}, and everyone's scores on it will be deleted. This can't be undone.`}
          confirmLabel="Delete quiz"
          onConfirm={() => router.delete(basePath)}
        />
      </AdminShell>
    </>
  )
}
